/**
 * Pagination — reusable page-navigation bar.
 * Rendered inside a table card's border-t footer area.
 */
interface PaginationProps {
  dark: boolean
  page: number
  totalPages: number
  totalItems: number
  itemLabel: string        // e.g. t('common.orders') or t('common.records')
  pageLabel: string        // e.g. t('common.page')
  ofLabel: string          // e.g. t('common.of')
  onFirst: () => void
  onPrev:  () => void
  onNext:  () => void
  onLast:  () => void
}

// SVG nav arrows
const IcoFirst = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v10M11 2L7 7l4 5"/>
  </svg>
)
const IcoPrev = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2L5 7l4 5"/>
  </svg>
)
const IcoNext = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 2l4 5-4 5"/>
  </svg>
)
const IcoLast = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 2v10M3 2l4 5-4 5"/>
  </svg>
)

export function Pagination({
  dark, page, totalPages, totalItems, itemLabel, pageLabel, ofLabel,
  onFirst, onPrev, onNext, onLast,
}: PaginationProps) {
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const btnBase = 'p-1.5 rounded-lg border transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'
  const btn = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`

  return (
    <div className={`flex items-center justify-between px-4 py-2.5 border-t ${dark ? 'border-white/6' : 'border-slate-100'}`}>
      <span className={`text-[11px] ${mutedTxt}`}>
        {pageLabel} {page} {ofLabel} {totalPages} · {totalItems.toLocaleString('es-ES')} {itemLabel}
      </span>
      <div className="flex gap-1">
        <button onClick={onFirst} disabled={page === 1}          className={btn} aria-label="First page"><IcoFirst /></button>
        <button onClick={onPrev}  disabled={page === 1}          className={btn} aria-label="Previous page"><IcoPrev /></button>
        <button onClick={onNext}  disabled={page === totalPages} className={btn} aria-label="Next page"><IcoNext /></button>
        <button onClick={onLast}  disabled={page === totalPages} className={btn} aria-label="Last page"><IcoLast /></button>
      </div>
    </div>
  )
}
